import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from 'react'

import { createGATracker, type GATrackingEngine } from './tracking'

import type { GATrackingOptions, GAEventData, BrandGAConfig } from './types'

/**
 * GA 追踪上下文类型
 */
interface GATrackingContextType {
  /** 追踪引擎实例 */
  engine: GATrackingEngine | null
  /** 手动发送追踪事件 */
  track: (eventData: GAEventData) => void
  /** 是否已启用 */
  isEnabled: boolean
}

const GATrackingContext = createContext<GATrackingContextType>({
  engine: null,
  track: () => {},
  isEnabled: false,
})

/**
 * GA 追踪 Provider Props
 */
interface GATrackingProviderProps {
  children: ReactNode
  /** 品牌 GA 配置 */
  config: BrandGAConfig
  /** 额外的追踪配置选项 */
  options?: Partial<GATrackingOptions>
}

/**
 * GA 追踪 Provider
 * @description 为 React 应用提供 GA 追踪功能
 *
 * @example
 * ```tsx
 * <GATrackingProvider config={brandConfig.analytics}>
 *   <App />
 * </GATrackingProvider>
 * ```
 */
export function GATrackingProvider({ children, config, options }: GATrackingProviderProps) {
  const engineRef = useRef<GATrackingEngine | null>(null)

  // 同步创建引擎，确保子组件 useEffect 中可立即调用 track()
  // createGATracker 为单例模式，重复调用返回同一实例
  // track() 仅推送到 window.dataLayer，无需等待 init()
  if (config.enabled && !engineRef.current) {
    const trackingOptions: GATrackingOptions = {
      ...options,
      modulePrefix: config.modulePrefix ?? options?.modulePrefix,
      businessEventContract: config.businessEventContract,
      debug: options?.debug,
      ...config.trackingOptions,
    }
    engineRef.current = createGATracker({ config: trackingOptions })
  }

  useEffect(() => {
    if (!config.enabled || !engineRef.current) {
      return
    }

    const engine = engineRef.current

    // init() 设置 DOM 点击监听器和 MutationObserver，必须在 effect 中执行
    engine.init()

    if (typeof window !== 'undefined') {
      ;(window as unknown as { gaTracking?: GATrackingEngine }).gaTracking = engine
    }

    return () => {
      engineRef.current = null
      if (typeof window !== 'undefined') {
        delete (window as unknown as { gaTracking?: GATrackingEngine }).gaTracking
      }
    }
  }, [config.enabled, config.modulePrefix, config.trackingOptions, options])

  const track = useCallback(
    (eventData: GAEventData) => {
      if (engineRef.current && config.enabled) {
        engineRef.current.track(eventData)
      }
    },
    [config.enabled]
  )

  const contextValue: GATrackingContextType = {
    engine: engineRef.current,
    track,
    isEnabled: config.enabled,
  }

  return <GATrackingContext.Provider value={contextValue}>{children}</GATrackingContext.Provider>
}

/**
 * 使用 GA 追踪 Hook
 *
 * @example
 * ```tsx
 * function PaymentButton() {
 *   const { track } = useGATracking();
 *
 *   const handleClick = () => {
 *     track({
 *       event: 'payment_initiated',
 *       action_name: 'Pay Now',
 *       module: 'checkout_form',
 *     });
 *   };
 *
 *   return <button onClick={handleClick}>Pay Now</button>;
 * }
 * ```
 */
export function useGATracking() {
  const context = useContext(GATrackingContext)

  if (!context) {
    throw new Error('useGATracking must be used within a GATrackingProvider')
  }

  return context
}

/**
 * GA 追踪属性生成器
 * @description 生成用于元素的 data-ga-* 属性
 *
 * @example
 * ```tsx
 * <button {...gaTrackProps({ action_name: 'Submit', module: 'form' })}>
 *   Submit
 * </button>
 * ```
 */
export function gaTrackProps(data: Partial<GAEventData>): Record<string, string> {
  const props: Record<string, string> = {}

  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      const attrName = `data-ga-${key.replace(/_/g, '-')}`
      props[attrName] = typeof value === 'object' ? JSON.stringify(value) : String(value)
    }
  }

  return props
}

export { GATrackingContext }
